import { Button, Spinner } from 'flowbite-react';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { undoRejectedCandidate } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { UndoRejectCandidateAPIInput } from 'types';
import { get } from 'lodash';

interface Props {
    userName: string;
    applicationId?: string;
    positionId?: string;
    onSubmit?: (data: UndoRejectCandidateAPIInput) => void;
    onFinish?: () => void;
    isLoading?: boolean;
}

const RecruiterCandidateUndoAutoReject = ({
    userName,
    applicationId,
    positionId,
    onSubmit,
    onFinish,
    isLoading: externalIsLoading
}: Props) => {
    const toast = useToast();
    const queryClient = useQueryClient();

    const undoAutoRejectFn = useMutation(['undoRejectedCandidate'], undoRejectedCandidate, {
        onSuccess() {
            toast({
                title: `Added candidate ${userName} back in pipeline`,
                type: 'success',
            });
            queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
            onFinish && onFinish();
        },
        onError(error: any) {
            // Extract error message from API response
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);

            // Check if it's the limit reached error
            if (errorMessage && errorMessage.includes('Cannot undo rejection. Maximum limit of 2 undo rejections has been reached')) {
                toast({
                    title: errorMessage,
                    type: 'error',
                    duration: 6000,
                });
            } else {
                toast({
                    title: `Error occurred: ${errorMessage}`,
                    type: 'error',
                });
            }
        },
    });

    return (
        <form
            onSubmit={e => {
                e.preventDefault();
                if (onSubmit) {
                    // Bulk action case - onSubmit is provided (applicationId may be undefined for bulk)
                    onSubmit({ applicationId: applicationId ?? '', positionId: positionId ?? '' });
                } else if (applicationId && positionId) {
                    // Single action case - use the mutation directly
                    undoAutoRejectFn.mutate({
                        applicationId,
                        positionId,
                    });
                }
            }}
        >
            <div>Are you sure you want to push {userName === 'Candidates' ? 'candidates' : 'candidate'} back in pipeline?</div>
            <Button
                type="submit"
                className="w-full mt-[20px] bg-[#7777E7]"
                color="purplePlaced"
                disabled={undoAutoRejectFn.isLoading || externalIsLoading}
            >
                {undoAutoRejectFn.isLoading || externalIsLoading ? <Spinner /> : 'Confirm'}
            </Button>
            <Button
                className="w-full mt-[20px]"
                color="light"
                onClick={onFinish}
                type="button"
            >
                Cancel
            </Button>
        </form>
    );
};

export default RecruiterCandidateUndoAutoReject;

